import { useEffect, useState } from 'react';
import axios from 'axios';
import { Save, FileText, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editable CMS pages — add a new entry here to expose another page in the admin UI.
const PAGES = [
  {
    key: 'home-statement',
    label: 'Home — Opening Statement',
    description: 'Shown on the public home page below the hero. Separate paragraphs with a blank line. The LAST paragraph becomes the gold dedication footer.',
    fields: ['body'],
  },
  {
    key: 'poker-room',
    label: 'Poker Room Page',
    description: 'The /poker-room page. Includes address/contact info on the side.',
    fields: ['title', 'subtitle', 'body', 'image_url', 'address', 'hours', 'phone', 'email'],
  },
  {
    key: 'about',
    label: 'About Red Dragon Page',
    description: 'The /about page.',
    fields: ['title', 'subtitle', 'body', 'image_url'],
  },
];

const FIELD_META = {
  title:      { label: 'Title',     type: 'input',    placeholder: 'Page title' },
  subtitle:   { label: 'Subtitle',  type: 'input',    placeholder: 'Optional tagline' },
  body:       { label: 'Body',      type: 'textarea', placeholder: 'Type your content here. Use a blank line to separate paragraphs.' },
  image_url:  { label: 'Image URL', type: 'input',    placeholder: 'https://...' },
  address:    { label: 'Address',   type: 'input',    placeholder: 'City, country' },
  hours:      { label: 'Hours',     type: 'input',    placeholder: 'Open daily 24/7' },
  phone:      { label: 'Phone',     type: 'input',    placeholder: '+1 555 0000' },
  email:      { label: 'Email',     type: 'input',    placeholder: 'info@example.com' },
};

export const SiteContentTab = ({ token }) => {
  const [activeKey, setActiveKey] = useState(PAGES[0].key);
  const [pageData, setPageData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const activePage = PAGES.find(p => p.key === activeKey);

  useEffect(() => {
    setLoading(true);
    setSavedAt(null);
    axios.get(`${API}/pages/${activeKey}`)
      .then(r => setPageData(r.data))
      .catch(() => setPageData({}))
      .finally(() => setLoading(false));
  }, [activeKey]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = activePage.fields.reduce((acc, f) => {
        acc[f] = pageData[f] ?? '';
        return acc;
      }, {});
      const { data } = await axios.put(`${API}/admin/pages/${activeKey}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPageData(data);
      setSavedAt(new Date());
    } catch (e) {
      alert('Failed to save: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setPageData(d => ({ ...d, [field]: value }));
    setSavedAt(null);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      {/* Page picker */}
      <div className="lg:col-span-1">
        <Label className="font-heading text-[10px] tracking-[0.25em] uppercase text-white/40">Pages</Label>
        <div className="mt-3 flex flex-col gap-1.5">
          {PAGES.map(p => (
            <button
              key={p.key}
              data-testid={`page-picker-${p.key}`}
              onClick={() => setActiveKey(p.key)}
              className={`text-left px-3.5 py-2.5 rounded-md border transition-colors ${
                activeKey === p.key
                  ? 'border-primary-main/40 bg-primary-main/10 text-white'
                  : 'border-white/[0.06] bg-white/[0.02] text-white/60 hover:text-white hover:bg-white/[0.04]'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="font-heading text-xs tracking-wide">{p.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Editor */}
      <div className="lg:col-span-3 rounded-2xl border border-white/[0.06] bg-background-surface p-6">
        {loading ? (
          <p className="text-white/40 text-sm">Loading…</p>
        ) : (
          <>
            <div className="mb-5">
              <h3 className="font-heading text-xl font-bold text-white" data-testid="page-editor-title">{activePage.label}</h3>
              {activePage.description && (
                <p className="mt-1 font-body text-xs text-white/40 leading-relaxed">{activePage.description}</p>
              )}
            </div>

            <div className="space-y-4">
              {activePage.fields.map(field => {
                const meta = FIELD_META[field];
                if (!meta) return null;
                const id = `field-${activeKey}-${field}`;
                if (meta.type === 'textarea') {
                  return (
                    <div key={field}>
                      <Label htmlFor={id} className="font-heading text-[10px] tracking-[0.2em] uppercase text-white/50">{meta.label}</Label>
                      <textarea
                        id={id}
                        data-testid={`page-field-${field}`}
                        value={pageData[field] || ''}
                        onChange={(e) => updateField(field, e.target.value)}
                        placeholder={meta.placeholder}
                        rows={14}
                        className="mt-2 w-full rounded-md border border-white/10 bg-background-base text-white/90 font-body text-sm leading-relaxed p-3 focus:outline-none focus:ring-1 focus:ring-primary-main/60 resize-y"
                      />
                    </div>
                  );
                }
                return (
                  <div key={field}>
                    <Label htmlFor={id} className="font-heading text-[10px] tracking-[0.2em] uppercase text-white/50">{meta.label}</Label>
                    <Input
                      id={id}
                      data-testid={`page-field-${field}`}
                      value={pageData[field] || ''}
                      onChange={(e) => updateField(field, e.target.value)}
                      placeholder={meta.placeholder}
                      className="mt-2 bg-background-base border-white/10 text-white/90"
                    />
                  </div>
                );
              })}
            </div>

            <div className="mt-7 flex items-center gap-3">
              <Button
                data-testid="page-save-btn"
                onClick={handleSave}
                disabled={saving}
                className="bg-secondary-main hover:bg-secondary-main/90 text-white font-heading tracking-wider"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
              {savedAt && (
                <span className="inline-flex items-center gap-1.5 text-emerald-400 text-xs font-heading tracking-wide" data-testid="page-save-confirmation">
                  <Check className="w-3.5 h-3.5" /> Saved · live on the site
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
